import {
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Button,
  Divider,
  Typography,
  Box,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonIcon from "@mui/icons-material/Person";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import { useState } from "react";
import DrWeedyLogo from "../assets/DrWeedyLogo";

interface ChatSession {
  id: string;
  title: string;
  lastMessage: string;
  timestamp: Date;
}

interface Props {
  chatSessions: ChatSession[];
  currentChatId: string;
  onNewChat: () => void;
  onSelectChat: (chatId: string) => void;
  onManageSubscription?: () => void;
  onLogout?: () => void;
  userEmail?: string;
}

export default function ChatSidebar({
  chatSessions,
  currentChatId,
  onNewChat,
  onSelectChat,
  onManageSubscription,
  onLogout,
  userEmail,
}: Props) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <IconButton
        onClick={() => setIsOpen(true)}
        className="xl:hidden fixed top-3 left-3 z-50 text-white bg-petrolio/80 hover:bg-petrolio backdrop-blur-sm shadow-lg"
        sx={{ borderRadius: "12px", width: "40px", height: "40px" }}
      >
        <MenuIcon />
      </IconButton>

      <div
        className={`
            fixed xl:relative inset-y-0 left-0 z-40 w-80
            bg-gradient-to-br from-petrolio-dark/95 via-[#1a313c]/95 to-[#04090a]/95
            backdrop-blur-xl shadow-2xl border-r border-white/10
            transform transition-all duration-500 ease-in-out
            ${isOpen ? "translate-x-0" : "-translate-x-full xl:translate-x-0"}
          `}
      >
        <div className="flex flex-col h-full relative z-10">
          <Box className="relative p-6 bg-gradient-to-br from-saluton/10 to-transparent border-b border-white/10">
            <div className="absolute inset-0 bg-gradient-to-r from-saluton/5 to-petrolio/10"></div>
            <div className="relative flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="relative">
                  <div className="absolute inset-0 bg-saluton/30 rounded-xl blur-md"></div>
                  <div className="relative">
                    <DrWeedyLogo width={40} height={40} />
                  </div>
                </div>
                <div>
                  <Typography
                    variant="h6"
                    sx={{
                      color: "#ffffff",
                      fontWeight: 700,
                      letterSpacing: "-0.02em",
                      fontFamily: "Oxanium, sans-serif",
                    }}
                  >
                    Dr. Weedy
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "rgba(226, 114, 91, 0.9)",
                      fontWeight: 500,
                    }}
                  >
                    by Saluton
                  </Typography>
                </div>
              </div>
              <IconButton
                onClick={() => setIsOpen(false)}
                sx={{
                  borderRadius: "10px",
                  color: "rgba(255,255,255,0.7)",
                  "&:hover": {
                    color: "#ffffff",
                    backgroundColor: "rgba(255,255,255,0.1)",
                  },
                  display: { xs: "block", xl: "none" },
                }}
              >
                <CloseIcon />
              </IconButton>
            </div>
          </Box>

          <div className="p-5">
            <Button
              onClick={onNewChat}
              variant="contained"
              startIcon={<AddIcon />}
              fullWidth
              sx={{
                background: "linear-gradient(135deg, #E2725B 0%, #CC3B2A 100%)",
                borderRadius: "16px",
                textTransform: "none",
                fontSize: "16px",
                fontWeight: 700,
                padding: "16px 24px",
                color: "#ffffff",
                border: "1px solid rgba(226, 114, 91, 0.3)",
                boxShadow: "0 8px 24px rgba(226, 114, 91, 0.3)",
                transition: "all 0.3s ease-in-out",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 10px 40px rgba(226, 114, 91, 0.4)",
                  background: "linear-gradient(135deg, #CC3B2A 0%, #E2725B 100%)",
                },
              }}
            >
              Nova Consulta
            </Button>
          </div>

          <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", mx: 2 }} />

          <div className="flex-1 overflow-y-auto px-3 pt-4">
            <Typography
              variant="overline"
              sx={{
                color: "rgba(255,255,255,0.5)",
                fontWeight: 700,
                fontSize: "11px",
                letterSpacing: "0.1em",
                paddingLeft: "12px",
                marginBottom: "16px",
                display: "block",
              }}
            >
              Consultas Anteriores
            </Typography>

            <List className="space-y-2">
              {chatSessions.map((session) => (
                <ListItem key={session.id} disablePadding>
                  <ListItemButton
                    selected={session.id === currentChatId}
                    onClick={() => {
                      onSelectChat(session.id);
                      setIsOpen(false);
                    }}
                    className={`
                      rounded-2xl mx-2 transition-all duration-300 ease-in-out
                    ${
                      session.id === currentChatId
                        ? "bg-gradient-to-r from-saluton/20 to-petrolio/20 border border-saluton/30 shadow-lg"
                        : "hover:bg-white/5 hover:border hover:border-white/10"
                    }
                    `}
                    sx={{
                      borderRadius: "16px",
                      marginBottom: "10px",
                      padding: "12px",
                    }}
                  >
                    <Box className="flex items-center space-x-3 w-full">
                      <div
                        className={`
                        p-2.5 rounded-xl transition-all duration-300
                        ${
                          session.id === currentChatId
                            ? "bg-gradient-to-br from-saluton/30 to-petrolio/30 shadow-md"
                            : "bg-white/5"
                        }
                      `}
                      >
                        <ChatBubbleOutlineIcon
                          sx={{
                            fontSize: "20px",
                            color:
                              session.id === currentChatId
                                ? "#E2725B"
                                : "rgba(255,255,255,0.6)",
                          }}
                        />
                      </div>
                      <ListItemText
                        primary={session.title}
                        secondary={session.lastMessage}
                        primaryTypographyProps={{
                          fontSize: "14px",
                          fontWeight: 600,
                          sx: {
                            color:
                              session.id === currentChatId
                                ? "#ffffff"
                                : "rgba(255,255,255,0.8)",
                          },
                        }}
                        secondaryTypographyProps={{
                          fontSize: "12px",
                          sx: {
                            color:
                              session.id === currentChatId
                                ? "rgba(255,255,255,0.6)"
                                : "rgba(255,255,255,0.4)",
                            marginTop: "4px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          },
                        }}
                      />
                    </Box>
                  </ListItemButton>
                </ListItem>
              ))}
            </List>

            {chatSessions.length === 0 && (
              <div className="text-center p-6 mt-12 mx-4">
                <div className="bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-sm rounded-3xl p-8 border border-white/10">
                  <ChatBubbleOutlineIcon
                    sx={{
                      fontSize: "48px",
                      color: "rgba(226, 114, 91, 0.6)",
                      marginBottom: "16px",
                    }}
                  />
                  <Typography
                    variant="h6"
                    sx={{
                      color: "#ffffff",
                      fontWeight: 600,
                      marginBottom: "8px",
                      fontFamily: "Oxanium, sans-serif",
                    }}
                  >
                    Primeira consulta?
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "rgba(255,255,255,0.7)",
                      lineHeight: 1.6,
                    }}
                  >
                    Inicie uma nova conversa com o Dr. Weedy para receber
                    orientações médicas personalizadas
                  </Typography>
                </div>
              </div>
            )}
          </div>

          {(onLogout || userEmail || onManageSubscription) && (
            <>
              <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", mx: 2 }} />
              <div className="p-4 mt-auto bg-gradient-to-t from-petrolio-dark/50 to-transparent">
                {onManageSubscription && (
                  <Button
                    onClick={() => {
                      onManageSubscription();
                      setIsOpen(false);
                    }}
                    variant="outlined"
                    fullWidth
                    startIcon={<CreditCardIcon />}
                    sx={{
                      mb: 2,
                      borderRadius: "16px",
                      textTransform: "none",
                      fontSize: "14px",
                      fontWeight: 600,
                      padding: "12px 20px",
                      color: "#ffffff",
                      borderColor: "rgba(226, 114, 91, 0.5)",
                      backgroundColor: "rgba(226, 114, 91, 0.08)",
                      transition: "all 0.3s ease-in-out",
                      "&:hover": {
                        borderColor: "#E2725B",
                        backgroundColor: "rgba(226, 114, 91, 0.18)",
                      },
                    }}
                  >
                    Minha assinatura
                  </Button>
                )}
                <div className="flex items-center justify-between bg-white/5 rounded-2xl p-3 border border-white/10">
                  {userEmail && (
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <div className="w-9 h-9 bg-gradient-to-br from-saluton to-saluton-dark rounded-xl flex items-center justify-center shadow-lg border border-saluton-light/30">
                        <PersonIcon
                          sx={{ color: "#ffffff", fontSize: "18px" }}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#ffffff",
                            fontWeight: 600,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {userEmail}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "rgba(69, 203, 166, 0.8)",
                            fontWeight: 500,
                          }}
                        >
                          Conectado
                        </Typography>
                      </div>
                    </div>
                  )}

                  {onLogout && (
                    <Button
                      onClick={() => {
                        onLogout();
                        setIsOpen(false);
                      }}
                      variant="outlined"
                      size="small"
                      startIcon={<LogoutIcon />}
                      className="border-coral/50 text-coral hover:bg-coral/20 hover:border-coral"
                      sx={{
                        borderRadius: "10px",
                        textTransform: "none",
                        fontSize: "11px",
                        fontWeight: 600,
                        minWidth: "auto",
                        px: 2,
                        py: 0.75,
                        transition: "all 0.3s",
                        "&:hover": {
                          transform: "scale(1.05)",
                        },
                      }}
                    >
                      Sair
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {isOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-md z-30 xl:hidden transition-all duration-300"
          onClick={() => setIsOpen(false)}
        />
      )}
    </>
  );
}
